import * as ActionTypes from '../actions/actionTypes';
import {DATA_SETS} from '../Constants';
import {
  calculateTableData,
  convertToGraphData,
  tableMetaData
} from '../utils/behaviour_models/shared/behaviour_models_utils';
import {
  calculateDIR,
  calculateFH,
  calculateFI,
  calculateFMC,
  calculateFROS,
  calculateFROSFlat,
  calculateFROS_NW,
  calculateFuelAvailabilityFactor,
  calculateFMCForPrescription,
  calculateSH
} from '../utils/behaviour_models/leaflet_80/calculations';
import {DEFAULT_FFL, DEFAULT_SLOPE, DEFAULT_WRF} from '../utils/behaviour_models/leaflet_80/constants';
import {runParityCalcsFromConfig} from '../utils/behaviour_models/shared/parity';
import {emptyGraphMap, extractTicks} from '../utils/point_utils';

export {calculateFMCForPrescription as calculateFMC};

const extractVariables = (state) => {
  return {
    slope: state.slope,
    fine_fuel_load: state.fine_fuel_load,
    wind_reduction_factor: state.wind_reduction_factor,
    lag_temp_rh: state.lag_temp_rh
  };
};

const VALUES_CONFIG = [
  { key: 'DIR', displayName: 'Direction of Spread', calc: calculateDIR, decPlaces: 0, arrow: 'fa-arrow-up' },
  { key: 'FMC', displayName: 'FMC (%)', calc: calculateFMC, decPlaces: 1 },
  {
    key: 'FAF',
    displayName: 'Fuel Availabilty Factor',
    calc: calculateFuelAvailabilityFactor,
    decPlaces: 1,
    nonFinal: true
  },
  { key: 'FROS_FLAT', displayName: 'FROS Flat (m/h)', calc: calculateFROSFlat, decPlaces: 0 },
  {
    key: 'FROS', displayName: 'FROS (m/h)', calc: calculateFROS, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 1000.0,
      leftColor: 'rgba(238, 46, 36, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  },
  { key: 'FROS_NW', displayName: 'FROS no wind (m/h)', calc: calculateFROS_NW, decPlaces: 0 },
  {
    key: 'FH', displayName: 'Flame Height (m)', calc: calculateFH, decPlaces: 1,
    colors: {
      scale: {
        '1.5': 'rgba(122, 193, 66, 0.7)',
        '4': 'rgba(248, 152, 41, 0.7)'
      },
      else: 'rgba(238, 46, 36, 0.7)'
    }
  },
  { key: 'SH', displayName: 'Scorch Height (m)', calc: calculateSH, decPlaces: 1 },
  {
    key: 'FI', displayName: 'Fireline Intensity (kW/m)', calc: calculateFI, decPlaces: 0,
    cellGraph: {
      min: 0.0,
      max: 15000.0,
      leftColor: 'rgba(248, 152, 41, 0.7)',
      rightColor: 'rgba(122, 193, 66, 0.2)'
    }
  }
];

const GRAPHS_CONFIG = [
  {
    title: 'Fuel Moisture Content',
    id: 'FMC',
    type: 'LineChart',
    YAxisLabel: '%',
    series: [
      {
        name: 'FMC(%)',
        dataKey: 'FMC',
        type: 'monotone',
        stroke: '#DC38C8',
        active: true
      }
    ]
  },
  {
    title: 'Forward Rate of Spread',
    id: 'FROS',
    type: 'LineChart',
    YAxisLabel: 'm/h',
    series: [
      {
        name: 'With Slope',
        dataKey: 'FROS',
        type: 'monotone',
        stroke: '#FF5131',
        active: true
      },
      {
        name: 'No Slope',
        dataKey: 'FROS_FLAT',
        type: 'monotone',
        stroke: '#FFB644',
        active: true
      },
      {
        name: 'No Slope, No Wind',
        dataKey: 'FROS_NW',
        type: 'monotone',
        stroke: '#008CC4',
        active: true
      }
    ]
  }
];

const initialState = {
  slope: DEFAULT_SLOPE,
  fine_fuel_load: DEFAULT_FFL,
  wind_reduction_factor: DEFAULT_WRF,
  lag_temp_rh: 0,
  table_data: {},
  graph_data: emptyGraphMap(GRAPHS_CONFIG),
  table_meta_data: tableMetaData(VALUES_CONFIG, DATA_SETS.LEAFLET_80),
  graph_meta_data: GRAPHS_CONFIG
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.SET_POINT_TOTAL_FUEL_LOAD:
      if (action.total_fuel_load !== undefined && action.total_fuel_load !== null) {
        return {
          ...state,
          fine_fuel_load: parseFloat(action.total_fuel_load).toFixed(0)
        };
      }
      return {
        ...state,
        fine_fuel_load: DEFAULT_FFL
      };

    case ActionTypes.SET_BEHAVIOUR_VARIABLES:
      if (action.data_set === DATA_SETS.LEAFLET_80) {
        return {
          ...state,
          ...action.variables
        };
      }
      break;
    case ActionTypes.CALCULATE_LEAFLET_80:
      const variables = extractVariables(state);
      const modifications = action.modifications ? action.modifications.properties.data : {};
      const new_table_data =
        calculateTableData(VALUES_CONFIG,
          action.include_history,
          action.short_future,
          action.weather_data.table_data,
          variables,
          modifications);
      const new_graph_data = convertToGraphData(GRAPHS_CONFIG, new_table_data);
      const ticks = extractTicks(new_graph_data, GRAPHS_CONFIG[0].id);

      return {
        ...state,
        ticks,
        graph_data: new_graph_data,
        table_data: new_table_data
      };

    default:
      return state;
  }
  return state;
};

export const runParityCalcs = (wd, variables) => runParityCalcsFromConfig(VALUES_CONFIG, wd, variables);

export default reducer;



// WEBPACK FOOTER //
// ./src/reducers/point_data_leaflet_80.js